import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, ArrowRightIcon as ArrowRight, ShieldCheckIcon as ShieldCheck, ScissorsIcon as Scissors, FootprintsIcon as Footprints, HouseIcon as House, GraduationCapIcon as GraduationCap, CalendarIcon as Calendar, ClockIcon as Clock, CurrencyDollarIcon as CurrencyDollar, CheckCircleIcon as CheckCircle, WarningIcon as Warning, StarIcon as Star, UploadSimpleIcon as UploadSimple, FilePdfIcon as FilePdf, DownloadSimpleIcon as DownloadSimple, TreeIcon as Tree } from "@phosphor-icons/react";
import StarRating from "@/components/StarRating";
import ProviderMediaCard from "@/components/ProviderMediaCard";
import ProviderCarePrefs from "@/components/ProviderCarePrefs";
import PricingInsights from "@/components/PricingInsights";
import { isNativeApp, openExternal, webOrigin } from "@/lib/nativeBridge";
import { publicFileUrl } from "@/lib/utils-app";
import { IntlSellerAgreementDialog, isIntlAgreementError } from "@/components/IntlSellerAgreementDialog";

/**
 * Provider self-onboarding + dashboard.
 *
 * One file covers the four routes a provider hits:
 *   - /provider/start  (no profile yet → onboarding wizard)
 *   - /provider/me     (profile exists → edit + services + status)
 *
 * Architecture choice: a single page that switches on
 * `/providers/mine` so we don't fork the routing tree for what is
 * really one journey ("set up your provider page → manage it").
 */
const KIND_OPTIONS = [
  { value: "trainer", label: "Trainer", Icon: GraduationCap, desc: "Obedience, behavior, sport, agility, protection." },
  { value: "groomer", label: "Groomer", Icon: Scissors, desc: "Bath, brush, cut, nails, mobile or shop." },
  { value: "walker",  label: "Walker",  Icon: Footprints, desc: "Solo or pack walks, drop-ins, pet-taxi." },
  { value: "sitter",  label: "Sitter",  Icon: House, desc: "Overnights, daycare, in-home or at yours." },
  { value: "field_host", label: "Zoomie Time Host", Icon: Tree, desc: "Rent out your private land by the hour for safe, off-leash zoomies." },
  { value: "other",   label: "Other",   Icon: Star, desc: "Pooper scooper, pet photographer, massage, taxi — tell us what you do." },
];

export default function ProviderOnboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    kind: "",
    specialty_label: "",
    business_name: "",
    tagline: "",
    bio: "",
    location_city: "",
    location_state: "",
    location_zip: "",
    service_area_zips: "",
    service_area_radius_miles: 10,
    phone: "",
    email: "",
    website_url: "",
    independent_business_ack: false,
    land_owner_ack: false,
    land: {
      acres: "",
      fenced: "fully",
      fence_height_ft: "",
      water_access: false,
      agility_equipment: false,
      parking_onsite: true,
      max_dogs: 4,
      terrain: "",
      hazards_note: "",
      buffer_minutes: 30,
      exact_address: "",
    },
    provider_agreement_ack: false,
  });
  const [licensingChecklist, setLicensingChecklist] = useState(null);
  const [licensingAnswers, setLicensingAnswers] = useState({});

  // Fetch the licensing self-attestation checklist whenever the
  // Provider picks a kind. Each kind has its own kind-specific item
  // (e.g. groomer = facility/mobile rules, walker = dog-walker license)
  // appended to the two shared items.
  useEffect(() => {
    if (!form.kind) {
      setLicensingChecklist(null);
      setLicensingAnswers({});
      return;
    }
    api.get(`/providers/licensing-checklist?kind=${form.kind}`)
      .then((r) => setLicensingChecklist(r.data))
      .catch(() => setLicensingChecklist(null));
    // Resetting answers when kind changes keeps the form honest —
    // a walker's answer to "dog-walker license" doesn't carry over
    // if they switch to "sitter".
    setLicensingAnswers({});
  }, [form.kind]);

  const allLicensingAnswered = !!licensingChecklist && licensingChecklist.items.every(
    (it) => !!licensingAnswers[it.id],
  );

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/providers/mine");
      if (r.data?.id) {
        setProvider(r.data);
        const s = await api.get("/providers/mine/services");
        setServices(s.data || []);
      } else {
        setProvider(null);
      }
    } catch {
      setProvider(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const setLand = (patch) => setForm({ ...form, land: { ...form.land, ...patch } });

  const submitSignup = async () => {
    if (!form.kind) return toast.error("Pick what kind of provider you are.");
    if (form.kind === "other" && form.specialty_label.trim().length < 3) return toast.error("Tell us what you do — e.g. 'Pooper Scooper' or 'Pet Photographer'.");
    if (!form.business_name.trim()) return toast.error("Business name is required.");
    if (form.kind === "field_host") {
      if (!Number(form.land.acres) || Number(form.land.acres) <= 0) return toast.error("Enter your field's approximate acreage.");
      if (!form.land.exact_address.trim()) return toast.error("Enter the field's exact address — it stays private until a booking is confirmed.");
      if (!form.land_owner_ack) return toast.error("Please certify you own or control this land and accept sole responsibility for it.");
    }
    if (!form.independent_business_ack) return toast.error("Please acknowledge you operate as an independent business.");
    if (!form.provider_agreement_ack) return toast.error("Please review and accept the Service Provider Agreement.");
    if (!allLicensingAnswered) return toast.error("Please answer every item in the licensing self-check.");
    try {
      const payload = {
        ...form,
        land: form.kind === "field_host" ? {
          ...form.land,
          acres: Number(form.land.acres),
          fence_height_ft: form.land.fence_height_ft === "" ? null : Number(form.land.fence_height_ft),
          max_dogs: Number(form.land.max_dogs) || 4,
          buffer_minutes: Number(form.land.buffer_minutes) || 30,
        } : null,
        service_area_zips: form.service_area_zips
          .split(",")
          .map((z) => z.trim())
          .filter(Boolean),
        service_area_radius_miles: Number(form.service_area_radius_miles) || null,
        licensing_attestation: (licensingChecklist?.items || []).map((it) => ({
          id: it.id,
          status: licensingAnswers[it.id],
        })),
      };
      const r = await api.post("/providers", payload);
      setProvider(r.data);
      toast.success("Submitted for review. We'll email you when approved.");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not create profile");
    }
  };

  if (loading) {
    return <AppShell><div className="text-[#6B6B6B]">Loading…</div></AppShell>;
  }

  // ----- Profile exists → manage view -----
  if (provider) {
    return (
      <AppShell>
        <div className="max-w-4xl">
          <ProviderAgreementReackBanner provider={provider} onRefresh={refresh} />
          <ProviderStatusBanner provider={provider} />
          <SetupChecklist provider={provider} services={services} />
          <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">{provider.specialty_label || KIND_OPTIONS.find(k => k.value === provider.kind)?.label || provider.kind}</p>
              <h1 className="font-spectral font-light text-4xl md:text-5xl tracking-tight leading-[1.05]" data-testid="provider-name">{provider.business_name}</h1>
              {provider.tagline && <p className="text-[#6B6B6B] mt-2">{provider.tagline}</p>}
              <p className="text-xs text-[#6B6B6B] font-mono mt-3">/provider/{provider.slug}</p>
            </div>
            {provider.status === "approved" && (
              <Button
                onClick={() => window.open(`/provider/${provider.slug}`, "_blank")}
                variant="outline"
                className="rounded-full"
                data-testid="provider-preview-btn"
              >
                Preview public page <ArrowRight size={13} weight="bold" className="ml-1" />
              </Button>
            )}
          </div>

          <div className="gold-hairline my-6 max-w-md" />

          <ProviderMediaCard provider={provider} onRefresh={refresh} />

          <PayoutsCard />

          <HowYouGetPaidCard />

          <EarningsCard />

          <InsuranceCard provider={provider} onRefresh={refresh} />

          <BackgroundCheckCard provider={provider} onRefresh={refresh} />

          <AvailabilityManager />

          <ServicesManager
            provider={provider}
            services={services}
            onRefresh={refresh}
          />

          <ProviderCarePrefs provider={provider} onRefresh={refresh} />

          <PricingInsights />

          <ReviewsManager provider={provider} />

          <ProviderLegalDocsCard signed={provider?.legal_acks?.provider_agreement} />
        </div>
      </AppShell>
    );
  }

  // ----- No profile → onboarding wizard -----
  return (
    <AppShell>
      <div className="max-w-3xl">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">List your services</p>
        <h1 className="font-spectral font-light text-4xl md:text-5xl tracking-tight leading-[1.05]">Set up your provider page</h1>
        <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
          Armstrong K9 Registry is a venue connecting customers with independent providers like you. You set your prices, your hours, and how you work. We handle the listing, the booking flow, and the payments.
        </p>
        <div className="gold-hairline my-6 max-w-md" />

        {/* Kind picker */}
        <div className="space-y-3 mb-8">
          <Label className="text-xs uppercase tracking-wider text-[#6B6B6B]">What kind of provider are you?</Label>
          <div className="grid sm:grid-cols-2 gap-3">
            {KIND_OPTIONS.map(({ value, label, Icon, desc }) => (
              <button
                key={value}
                type="button"
                onClick={() => setForm({ ...form, kind: value })}
                data-testid={`kind-${value}`}
                className={`text-left p-4 rounded-xl border transition-colors ${
                  form.kind === value
                    ? "border-gold bg-cream shadow-gold-glow"
                    : "border-[#EAEAEA] hover:border-gold/55 hover:bg-[#FAFAFA]"
                }`}
              >
                <div className="flex items-center gap-3 mb-1">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white ring-1 ring-[#E2DAC2]">
                    <Icon size={18} weight="duotone" className="text-[#8A6A00]" />
                  </span>
                  <span className="font-semibold text-ink">{label}</span>
                </div>
                <p className="text-[12.5px] text-[#6B6B6B] leading-snug">{desc}</p>
              </button>
            ))}
          </div>
          {form.kind === "other" && (
            <div className="bg-cream border border-[#E2DAC2] rounded-xl p-4 space-y-2" data-testid="specialty-label-block">
              <Label className="text-xs uppercase tracking-wider text-[#8A6A00]">What do you do? (shown to customers) *</Label>
              <Input
                value={form.specialty_label}
                maxLength={40}
                placeholder="e.g. Pooper Scooper, Pet Photographer, Canine Massage"
                onChange={(e) => setForm({ ...form, specialty_label: e.target.value })}
                data-testid="specialty-label-input"
              />
              <p className="text-[12px] text-[#6B6B6B] leading-snug">
                This label appears on your directory card and public page instead of "Other."
                A background check is optional for your category, but strongly encouraged —
                it earns the green "Cleared" badge customers filter by.
              </p>
            </div>
          )}
          {form.kind === "field_host" && (
            <div className="bg-cream border border-[#E2DAC2] rounded-xl p-4 space-y-4" data-testid="land-profile-block">
              <p className="text-xs uppercase tracking-wider font-bold text-[#8A6A00] flex items-center gap-1.5">
                <Tree size={13} weight="duotone" /> Your field
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Acres *</Label>
                  <Input type="number" min="0.1" step="0.1" value={form.land.acres} placeholder="2.5" onChange={(e) => setLand({ acres: e.target.value })} data-testid="land-acres-input" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Fencing *</Label>
                  <select
                    value={form.land.fenced}
                    onChange={(e) => setLand({ fenced: e.target.value })}
                    className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
                    data-testid="land-fenced-select"
                  >
                    <option value="fully">Fully fenced</option>
                    <option value="partially">Partially fenced</option>
                    <option value="none">Not fenced</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Fence height (ft)</Label>
                  <Input type="number" min="0" step="0.5" value={form.land.fence_height_ft} placeholder="6" onChange={(e) => setLand({ fence_height_ft: e.target.value })} data-testid="land-fence-height-input" />
                </div>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-[13px]">
                {[
                  ["water_access", "Water for dogs"],
                  ["agility_equipment", "Agility equipment"],
                  ["parking_onsite", "On-site parking"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form.land[key]} onChange={(e) => setLand({ [key]: e.target.checked })} className="h-4 w-4 accent-[#8A6A00]" data-testid={`land-${key}-toggle`} />
                    {label}
                  </label>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Max dogs per visit</Label>
                  <Input type="number" min="1" max="20" value={form.land.max_dogs} onChange={(e) => setLand({ max_dogs: e.target.value })} data-testid="land-max-dogs-input" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Gap between visits (min)</Label>
                  <Input type="number" min="0" max="120" step="5" value={form.land.buffer_minutes} onChange={(e) => setLand({ buffer_minutes: e.target.value })} data-testid="land-buffer-input" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Terrain</Label>
                <Input value={form.land.terrain} placeholder="Open grass field with some woods and a small hill" onChange={(e) => setLand({ terrain: e.target.value })} data-testid="land-terrain-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Hazards guests should know about</Label>
                <Input value={form.land.hazards_note} placeholder="Pond on the north side; livestock in the adjacent pasture" onChange={(e) => setLand({ hazards_note: e.target.value })} data-testid="land-hazards-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">Exact address (private) *</Label>
                <Input value={form.land.exact_address} placeholder="1234 County Rd 12, Hastings, MN 55033" onChange={(e) => setLand({ exact_address: e.target.value })} data-testid="land-exact-address-input" />
                <p className="text-[11px] text-[#6B6B6B] mt-1">Never shown publicly — guests see your city and approximate area only. The exact address is revealed after their booking is confirmed and paid.</p>
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer bg-white border border-[#E2DAC2] rounded-lg p-3 text-[12.5px] text-ink leading-relaxed">
                <input type="checkbox" checked={form.land_owner_ack} onChange={(e) => setForm({ ...form, land_owner_ack: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#8A6A00]" data-testid="land-owner-ack-checkbox" />
                <span>
                  <strong>I certify that I own or legally control this land</strong>, that listing it complies with my local zoning, HOA, and land-use rules, and that I am <strong>solely responsible</strong> for its condition and safety. All details are self-reported by me and not verified by Armstrong K9 Registry, which provides <strong>no damage protection, no insurance, and no guarantees</strong>. I'm advised to carry my own homeowner's/farm/umbrella insurance covering paid guest use.
                </span>
              </label>
            </div>
          )}

        </div>

        <div className="space-y-4">
          <Field label="Business name" required>
            <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="business-name-input" />
          </Field>
          <Field label="Tagline (one-liner shown on cards)">
            <Input value={form.tagline} placeholder="e.g. Positive-reinforcement obedience for puppies and rescue dogs" onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="tagline-input" />
          </Field>
          <Field label="About your business">
            <Textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="bio-input" />
          </Field>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="City" required>
              <Input value={form.location_city} onChange={(e) => setForm({ ...form, location_city: e.target.value })} data-testid="city-input" />
            </Field>
            <Field label="State" required>
              <Input maxLength={2} value={form.location_state} placeholder="MN" onChange={(e) => setForm({ ...form, location_state: e.target.value.toUpperCase() })} data-testid="state-input" />
            </Field>
            <Field label="ZIP" required>
              <Input value={form.location_zip} onChange={(e) => setForm({ ...form, location_zip: e.target.value })} data-testid="zip-input" />
            </Field>
          </div>

          <Field label="Service area — extra ZIPs you'll travel to (comma-separated)">
            <Input value={form.service_area_zips} placeholder="55101, 55102, 55103" onChange={(e) => setForm({ ...form, service_area_zips: e.target.value })} data-testid="service-zips-input" />
          </Field>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Public phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="phone-input" />
            </Field>
            <Field label="Contact email" required>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="email-input" />
            </Field>
            <Field label="Website (optional)">
              <Input value={form.website_url} placeholder="https://" onChange={(e) => setForm({ ...form, website_url: e.target.value })} data-testid="website-input" />
            </Field>
          </div>
        </div>

        {/* The venue moat — provider side. This text is intentionally
            verbatim from the PRD and must not be softened. */}
        <div className="mt-6 rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="independent-ack"
              checked={form.independent_business_ack}
              onCheckedChange={(c) => setForm({ ...form, independent_business_ack: !!c })}
              data-testid="ack-independent-business"
            />
            <Label htmlFor="independent-ack" className="text-[13px] text-ink leading-relaxed cursor-pointer">
              <strong>I acknowledge:</strong> I am an independent business. Armstrong K9 Registry is not my employer. I am responsible for my own insurance, licensing, taxes, training methods, scheduling, and conduct. Armstrong K9 Registry is a venue connecting me with customers — it is not a party to my transactions with them.
            </Label>
          </div>
        </div>

        {/* Service Provider Agreement — click-wrap acceptance. The PDF
            is rendered server-side at /api/providers/agreement.pdf so
            providers can read the full doc before they sign. */}
        <div className="mt-4 rounded-xl border border-gold/55 bg-[#FBF7EC] p-4" data-testid="provider-agreement-block">
          <div className="flex items-start gap-3">
            <Checkbox
              id="provider-agreement-ack"
              checked={form.provider_agreement_ack}
              onCheckedChange={(c) => setForm({ ...form, provider_agreement_ack: !!c })}
              data-testid="ack-provider-agreement"
            />
            <Label htmlFor="provider-agreement-ack" className="text-[13px] text-ink leading-relaxed cursor-pointer">
              <strong>I have read and accept the{" "}
                <a
                  href="/api/providers/agreement.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-crimson font-semibold"
                  data-testid="provider-agreement-pdf-link"
                >
                  Service Provider Agreement (v2026-06-03)
                </a>
              </strong>. I understand Armstrong K9 Registry is a marketplace/venue, is not a party to my service contracts with customers, does not adjudicate disputes between me and my customers, and only requires of me what my own state/local law independently requires. Best-practice items (insurance, background checks, certifications) are recommended, not required.
            </Label>
          </div>
        </div>

        {/* Licensing self-attestation. Armstrong never verifies these
            answers — the checklist exists to document that we asked
            the question. Each Provider picks "applies + I'm compliant",
            "doesn't apply", or "I'll check" per item. */}
        {licensingChecklist && (
          <div className="mt-4 rounded-xl border border-[#EAEAEA] bg-white p-4" data-testid="licensing-checklist-block">
            <div className="mb-3">
              <p className="font-spectral text-lg tracking-tight">Quick licensing self-check</p>
              <p className="text-[12px] text-[#6B6B6B] mt-1 leading-relaxed">
                Armstrong does <strong>not</strong> verify or require any of these — your own state, county, or city does. Answer honestly and (where it applies) use the link to check the rules where you operate. Pick <em>"I'll check"</em> for anything you haven't confirmed yet; it doesn't block your listing, it just documents that we asked.
              </p>
            </div>
            <div className="space-y-3">
              {licensingChecklist.items.map((item) => (
                <LicensingItemRow
                  key={item.id}
                  item={item}
                  value={licensingAnswers[item.id]}
                  onChange={(status) =>
                    setLicensingAnswers((a) => ({ ...a, [item.id]: status }))
                  }
                />
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            onClick={submitSignup}
            disabled={!form.kind || !form.business_name.trim() || !form.email.trim() || !form.independent_business_ack || !form.provider_agreement_ack || !allLicensingAnswered}
            className="rounded-full bg-ink hover:bg-crimson text-white"
            data-testid="provider-submit-btn"
          >
            Submit for review
            <ArrowRight size={14} weight="bold" className="ml-1.5" />
          </Button>
          <p className="text-xs text-[#6B6B6B]">After review, we'll set up your Stripe payouts + booking calendar.</p>
        </div>

        <ProviderLegalDocsCard variant="signup" />
      </div>
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-1.5 block">
        {label}{required && <span className="text-crimson"> *</span>}
      </Label>
      {children}
    </div>
  );
}

const LICENSING_STATUS_OPTIONS = [
  {
    value: "applies_compliant",
    label: "Yes — applies, I'm compliant",
    tone: "border-emerald-300 bg-emerald-50 text-emerald-900",
  },
  {
    value: "does_not_apply",
    label: "Doesn't apply in my jurisdiction",
    tone: "border-[#EAEAEA] bg-[#FAFAFA] text-[#3F3F3F]",
  },
  {
    value: "checking",
    label: "I'll check before accepting bookings",
    tone: "border-amber-300 bg-amber-50 text-amber-900",
  },
];

function LicensingItemRow({ item, value, onChange }) {
  return (
    <div
      className="border border-[#EAEAEA] rounded-lg p-3"
      data-testid={`licensing-item-${item.id}`}
    >
      <p className="font-semibold text-ink text-[13.5px]">{item.label}</p>
      <p className="text-[12px] text-[#6B6B6B] mt-1 leading-snug">
        {item.when_it_applies}
      </p>
      {item.lookup_url && (
        <a
          href={item.lookup_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11.5px] text-crimson hover:underline font-semibold inline-block mt-1.5"
          data-testid={`licensing-item-${item.id}-lookup`}
        >
          {item.lookup_label || "Look it up →"}
        </a>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        {LICENSING_STATUS_OPTIONS.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
                selected
                  ? opt.tone
                  : "border-[#EAEAEA] bg-white text-[#6B6B6B] hover:border-[#C9A227]/55"
              }`}
              data-testid={`licensing-item-${item.id}-status-${opt.value}`}
              aria-pressed={selected}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Legal-doc download panel rendered in two places:
 *   - In the signup wizard (`variant="signup"`) below the submit
 *     button, framed as "Need to show your lawyer?"
 *   - In the provider manage dashboard (no variant) framed as
 *     "Legal documents on file" with the same download links.
 *
 * Both PDFs are served by the backend at `/api/providers/*.pdf` —
 * `?download=1` forces an attachment Save-As dialog instead of an
 * in-browser preview. We expose both so the Provider can preview
 * before they email it to counsel.
 */
function ProviderLegalDocsCard({ variant, signed }) {
  const isSignup = variant === "signup";
  const docs = [
    {
      label: "Service Provider Agreement",
      version: "v2026-06-03",
      desc: "The 14-article click-wrap agreement you accept at signup. Defines Armstrong's venue-only role, your independent-business representation, the uncapped indemnity flowing from you to Armstrong, and your operating rules for Live Walk GPS.",
      preview: "/api/providers/agreement.pdf",
      download: "/api/providers/agreement.pdf?download=1",
      testid: "legal-docs-agreement",
    },
    {
      label: "Licensing Self-Attestation Checklist",
      version: "v2026-02-04",
      desc: "Companion document. Lists every licensing question Armstrong asks at signup, grouped by kind, with the same lookup links the wizard uses.",
      preview: "/api/providers/licensing-checklist.pdf",
      download: "/api/providers/licensing-checklist.pdf?download=1",
      testid: "legal-docs-checklist",
    },
    {
      label: "Background Check & FCRA Adverse Action Policy",
      version: "v2026-02-04",
      desc: "If you ever buy the $39 background-check badge, this is the workflow you'll be subject to: consent capture, the three outcomes, the pre-adverse / adverse-action process, the candidate-dispute path through Checkr, and the refund mechanics.",
      preview: "/api/providers/background-check-policy.pdf",
      download: "/api/providers/background-check-policy.pdf?download=1",
      testid: "legal-docs-bgcheck",
    },
  ];

  return (
    <div
      className={
        isSignup
          ? "mt-8 rounded-xl border border-[#EAEAEA] bg-[#FAFAFA] p-4"
          : "mt-10 rounded-xl border border-[#EAEAEA] bg-white p-5"
      }
      data-testid={isSignup ? "provider-legal-docs-signup" : "provider-legal-docs"}
    >
      <div className="flex items-start gap-3 mb-3">
        <FilePdf size={20} weight="duotone" className="text-crimson flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-spectral text-lg tracking-tight">
            {isSignup ? "Need to show your lawyer first?" : "Legal documents on file"}
          </p>
          <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-snug">
            {isSignup
              ? "Both documents below are public — download, send them to counsel, accountant, or your insurance broker for review before you accept."
              : "These are the documents you accepted (or will accept) when you set up your provider profile. Download to share with your lawyer, accountant, or insurance broker."}
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {docs.map((d) => (
          <div
            key={d.testid}
            className="border border-[#EAEAEA] bg-white rounded-lg p-3 flex flex-col"
            data-testid={d.testid}
          >
            <p className="font-semibold text-ink text-[13.5px] leading-tight">{d.label}</p>
            <p className="text-[11px] uppercase tracking-wider text-gold font-bold mt-0.5">{d.version}</p>
            <p className="text-[12px] text-[#6B6B6B] mt-1.5 leading-snug flex-1">{d.desc}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <a
                href={d.preview}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold border border-[#EAEAEA] hover:border-gold/55 text-ink hover:bg-cream/40 transition-colors"
                data-testid={`${d.testid}-preview`}
              >
                Preview
              </a>
              <a
                href={d.download}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-ink hover:bg-crimson text-white transition-colors"
                data-testid={`${d.testid}-download`}
              >
                <DownloadSimple size={12} weight="bold" /> Download PDF
              </a>
            </div>
          </div>
        ))}
      </div>
      {!isSignup && signed?.version && signed?.signed_at && (
        <div
          className="mt-4 rounded-lg border border-[#0F8043]/40 bg-[#EBF7EE] p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="legal-docs-executed-agreement"
        >
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-[#0F8043] font-bold flex items-center gap-1">
              <ShieldCheck size={11} weight="fill" /> Your signed copy
            </p>
            <p className="text-[13px] text-ink font-semibold leading-tight mt-1">
              Executed Service Provider Agreement
            </p>
            <p className="text-[11.5px] text-[#3F3F3F] mt-0.5 leading-snug">
              Personalized PDF — your legal name, IP, accept-timestamp, and v{signed.version} stamped on the cover. Keep this for your records.
            </p>
          </div>
          <a
            href="/api/me/consents/executed.pdf?kind=service_provider_agreement"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-[#0F8043] hover:bg-[#0a6635] text-white transition-colors flex-shrink-0"
            data-testid="legal-docs-executed-agreement-download"
          >
            <DownloadSimple size={12} weight="bold" /> Download executed copy
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Non-blocking re-ack banner for providers who signed up before
 * v2026-02-04 of the Service Provider Agreement. The backend records
 * `legal_acks.provider_agreement.version` — if it's missing or older
 * than the current version, we surface this banner with a one-click
 * accept that calls POST /providers/mine/agreement/accept.
 *
 * Soft-gate by design: we don't disable their dashboard or hide them
 * from discovery. Per the venue posture, providers' livelihoods don't
 * pause for a Terms update — we just nag.
 *
 * The current version is fetched from GET /providers/agreement/version
 * (single source of truth on the backend) — a hardcoded copy here once
 * drifted and made the banner impossible to dismiss.
 */
function ProviderAgreementReackBanner({ provider, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  useEffect(() => {
    api
      .get("/providers/agreement/version")
      .then((r) => setCurrentVersion(r.data?.version || null))
      .catch(() => setCurrentVersion(null));
  }, []);
  const signed = provider?.legal_acks?.provider_agreement;
  if (!currentVersion || signed?.version === currentVersion || dismissed) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/providers/mine/agreement/accept", {});
      toast.success("Thanks — your provider agreement is up to date.");
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not record your acceptance.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-6 rounded-xl border border-gold/55 bg-[#FBF7EC] p-4"
      data-testid="provider-agreement-reack-banner"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck size={20} weight="duotone" className="text-[#8A6A00] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#5A4500] text-sm">
            New Service Provider Agreement (v{currentVersion})
          </p>
          <p className="text-[12.5px] text-[#5A4500]/85 mt-1 leading-relaxed">
            We've published an updated Service Provider Agreement that makes Armstrong K9 Registry's <strong>venue-only role</strong> explicit and confirms that best-practice items (insurance, background checks, certifications) are <strong>recommended, not required</strong> — only your own state and local law can require them.{" "}
            <a
              href="/api/providers/agreement.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
              data-testid="provider-agreement-reack-pdf-link"
            >
              Read the full PDF →
            </a>
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              size="sm"
              onClick={accept}
              disabled={busy}
              className="rounded-full bg-ink hover:bg-crimson text-white text-[12px]"
              data-testid="provider-agreement-reack-accept"
            >
              {busy ? "Recording…" : "I accept the new agreement"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDismissed(true)}
              className="rounded-full text-[12px] text-[#6B6B6B] hover:text-ink"
              data-testid="provider-agreement-reack-dismiss"
            >
              Remind me later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}



/**
 * Setup checklist banner — top-of-dashboard nudge.
 *
 * Renders only for approved providers, only while at least one
 * required step is incomplete. Surfaces all friction at once so
 * a new provider has a clear linear path from "approved" → "first
 * paid booking". Auto-hides once everything is done.
 */
function SetupChecklist({ provider, services }) {
  const [acct, setAcct] = useState(null);
  const [loadingAcct, setLoadingAcct] = useState(true);
  const [busy, setBusy] = useState(false);
  const [intlOpen, setIntlOpen] = useState(false);

  useEffect(() => {
    if (!provider || provider.status !== "approved") {
      setLoadingAcct(false);
      return;
    }
    api.get("/connect/account", { silent: true })
      .then((r) => setAcct(r.data))
      .catch(() => setAcct({ onboarded: false, account: {} }))
      .finally(() => setLoadingAcct(false));
  }, [provider]);

  if (!provider || provider.status !== "approved" || loadingAcct) return null;

  const stripeDone = acct?.onboarded === true;
  const hasService = (services?.length || 0) > 0;
  const showBgCheck = true; // optional trust badge, offered to every kind
  const bgEncouraged = provider.kind === "walker" || provider.kind === "sitter" || provider.kind === "other";
  const bgStatus = provider.background_check?.status || "not_started";
  const bgDone = bgStatus === "clear" || bgStatus === "requested" || bgStatus === "consider";

  const allDone = stripeDone && hasService; // optional items never block "done"
  if (allDone) return null;

  const connectStripe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/connect/account", {
        return_url: `${webOrigin()}/connect/return?flow=provider`,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      if (isIntlAgreementError(e)) {
        setIntlOpen(true);
      } else {
        toast.error(formatApiError(e) || "Could not start Stripe setup");
      }
    } finally {
      setBusy(false);
    }
  };

  const items = [
    {
      key: "stripe",
      done: stripeDone,
      label: "Connect Stripe so you can accept paid bookings",
      sub: stripeDone
        ? "Done — payments will deposit to your bank on Stripe's standard schedule."
        : "Required. Customers can see your profile but can't book paid services until this is complete. ~5 minutes — have a photo ID, your bank routing & account numbers, and your SSN (or EIN) handy.",
      cta: stripeDone ? null : { label: busy ? "Redirecting…" : "Connect with Stripe", onClick: connectStripe, disabled: busy },
    },
    {
      key: "service",
      done: hasService,
      label: "Publish at least one service",
      sub: hasService
        ? `Done — ${services.length} service${services.length === 1 ? "" : "s"} published.`
        : "Without published services, your public profile has no booking widget. Scroll down to the Services card to add your first.",
      cta: hasService ? null : { label: "Add a service", href: "#services-card" },
    },
    ...(showBgCheck ? [{
      key: "bgcheck",
      done: bgDone,
      label: "Start background check (optional, $39)",
      sub: bgDone
        ? "Done — unlocks the green 'Background check cleared' trust badge when Checkr completes the screen."
        : bgEncouraged
          ? "Optional but strongly recommended for walkers & sitters — you enter homes and take possession of dogs. Adds a green 'Cleared' badge, lifts your search ranking, and $25 credits back on your first paid booking."
          : "Optional trust badge. Adds a green 'Cleared' badge to your profile, lifts your search ranking, and $25 credits back on your first paid booking.",
      cta: bgDone ? null : { label: "Start ($39)", href: "#bg-check-card" },
    }] : []),
  ];

  const remaining = items.filter((i) => !i.done).length;

  return (
    <div
      className="mb-6 rounded-xl border-2 border-gold/55 bg-gradient-to-br from-cream/60 to-white p-5"
      data-testid="setup-checklist"
    >
      <IntlSellerAgreementDialog open={intlOpen} onOpenChange={setIntlOpen} onAccepted={connectStripe} />
      <div className="flex items-start gap-3 mb-3">
        <CheckCircle size={22} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-spectral text-xl tracking-tight">
            Finish setup to start earning ({items.filter((i) => i.done).length}/{items.length})
          </p>
          <p className="text-[12.5px] text-[#6B6B6B] mt-0.5 leading-snug">
            {remaining === 1
              ? "One step left — finish this and your profile is fully bookable."
              : `${remaining} steps left. Get these done in the next few minutes and you're ready for your first paying customer.`}
          </p>
        </div>
      </div>
      <ol className="space-y-2.5 mt-4">
        {items.map((item, idx) => (
          <li
            key={item.key}
            className={`flex items-start gap-3 p-3 rounded-lg border ${
              item.done
                ? "border-emerald-200 bg-emerald-50"
                : "border-[#EAEAEA] bg-white"
            }`}
            data-testid={`setup-checklist-item-${item.key}`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {item.done ? (
                <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-[#C4C4C4] flex items-center justify-center text-[10px] font-bold text-[#6B6B6B]">
                  {idx + 1}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-[13.5px] font-semibold ${item.done ? "text-emerald-900 line-through opacity-70" : "text-ink"}`}>
                {item.label}
              </p>
              <p className={`text-[12px] mt-0.5 leading-snug ${item.done ? "text-emerald-900/70" : "text-[#6B6B6B]"}`}>
                {item.sub}
              </p>
            </div>
            {item.cta && (
              item.cta.href ? (
                <a
                  href={item.cta.href}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-ink hover:bg-crimson text-white transition-colors"
                  data-testid={`setup-checklist-cta-${item.key}`}
                >
                  {item.cta.label}
                </a>
              ) : (
                <Button
                  size="sm"
                  onClick={item.cta.onClick}
                  disabled={item.cta.disabled}
                  className="rounded-full bg-ink hover:bg-crimson text-white text-[12px] flex-shrink-0"
                  data-testid={`setup-checklist-cta-${item.key}`}
                >
                  {item.cta.label}
                </Button>
              )
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}



function ProviderStatusBanner({ provider }) {
  if (provider.status === "approved") return null;
  const map = {
    pending: {
      tone: "border-[#C9A227]/40 bg-gradient-to-r from-[#FBF7EC] to-[#F4E5B0]/40 text-[#8A6A00]",
      title: "Under review",
      body: "Your profile is in the curation queue. We'll email you the moment it's approved (usually within 48 hours during local-launch).",
    },
    suspended: {
      tone: "border-[#C1272D]/40 bg-[#FFF0F0] text-[#7A1A1A]",
      title: "Suspended",
      body: provider.status_reason || "Your listing is temporarily off the platform. Reach out to support to resolve.",
    },
    rejected: {
      tone: "border-[#C1272D]/40 bg-[#FFF0F0] text-[#7A1A1A]",
      title: "Not approved",
      body: provider.status_reason || "We weren't able to approve this profile. Reach out to support if you'd like to discuss.",
    },
  };
  const meta = map[provider.status];
  if (!meta) return null;
  return (
    <div className={`mb-6 rounded-xl border p-4 ${meta.tone}`} data-testid={`provider-status-${provider.status}`}>
      <div className="flex items-start gap-3">
        <ShieldCheck size={20} weight="duotone" className="flex-shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-semibold text-sm">{meta.title}</p>
          <p className="text-[12.5px] mt-0.5 opacity-90">{meta.body}</p>
        </div>
      </div>
    </div>
  );
}

function ServicesManager({ provider, services, onRefresh }) {
  const [form, setForm] = useState({
    name: "",
    duration_minutes: 60,
    price: 0,
    description: "",
    kind: "one_time",
    cadences: ["weekly"],
    max_sessions: 12,
    tier_4: 5,
    tier_8: 10,
    tier_12: 15,
    photos: [], // up to 3 storage_path refs
    virtual: false,
  });
  const [open, setOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const servicePhotoRef = React.useRef(null);

  const onPickServicePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const remaining = 3 - form.photos.length;
    if (remaining <= 0) {
      toast.error("3 photos max per service.");
      return;
    }
    setPhotoBusy(true);
    try {
      const uploaded = [];
      for (const file of files.slice(0, remaining)) {
        if (!file.type.startsWith("image/")) continue;
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const ref = r.data?.storage_path || r.data?.url;
        if (ref) uploaded.push(ref);
      }
      setForm({ ...form, photos: [...form.photos, ...uploaded] });
      toast.success(`Added ${uploaded.length} photo${uploaded.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  const removeServicePhoto = (idx) => {
    setForm({ ...form, photos: form.photos.filter((_, i) => i !== idx) });
  };

  const addService = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.price || form.price <= 0) return toast.error("Price must be greater than 0");
    const payload = {
      name: form.name,
      duration_minutes: Number(form.duration_minutes),
      price: Number(form.price),
      description: form.description,
      kind: form.kind,
      photos: form.photos,
      virtual: !!form.virtual,
    };
    if (form.kind === "recurring") {
      payload.recurring_config = {
        cadences: form.cadences,
        max_sessions: Number(form.max_sessions),
        discount_tiers: {
          "4": Number(form.tier_4) || 0,
          "8": Number(form.tier_8) || 0,
          "12": Number(form.tier_12) || 0,
        },
      };
    }
    try {
      await api.post("/providers/mine/services", payload);
      setForm({ ...form, name: "", price: 0, description: "", photos: [] });
      setOpen(false);
      toast.success("Service added");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e) || "Could not add");
    }
  };

  const toggleCadence = (c) => {
    const has = form.cadences.includes(c);
    const next = has ? form.cadences.filter((x) => x !== c) : [...form.cadences, c];
    setForm({ ...form, cadences: next.length ? next : ["weekly"] });
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this service?")) return;
    try {
      await api.delete(`/providers/mine/services/${id}`);
      toast.success("Removed");
      onRefresh();
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div data-testid="services-manager" id="services-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-spectral text-2xl tracking-tight">Services</h2>
        {!open && (
          <Button size="sm" onClick={() => setOpen(true)} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="add-service-btn">
            <Plus size={13} weight="bold" className="mr-1.5" /> Add service
          </Button>
        )}
      </div>

      {open && (
        <div className="bg-white border border-[#EAEAEA] rounded-xl p-4 mb-4 space-y-3" data-testid="service-form">
          <Field label="Service name" required>
            <Input value={form.name} placeholder="30-min walk · Full groom · 1-hour training session" onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input" />
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Duration (mins)">
              <Input type="number" min="15" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="service-duration-input" />
            </Field>
            <Field label="Price (USD)" required>
              <Input type="number" min="1" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="service-price-input" />
            </Field>
            <Field label="Type">
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="service-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One-time</SelectItem>
                  <SelectItem value="recurring">Recurring</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Description">
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="service-desc-input" />
          </Field>
          <label className="flex items-start gap-2.5 rounded-lg border border-[#EAEAEA] bg-[#FBFBFB] p-3 cursor-pointer" data-testid="service-virtual-toggle">
            <input
              type="checkbox"
              checked={!!form.virtual}
              onChange={(e) => setForm({ ...form, virtual: e.target.checked })}
              className="mt-0.5 accent-[#0E0E0E]"
              data-testid="service-virtual-checkbox"
            />
            <span className="text-[12.5px] leading-snug text-ink">
              <strong>Video session</strong> — this service happens over a video call
              (virtual training consults, behavior assessments, check-ins). We create
              the video room automatically when a booking is paid; both of you get a
              Join button in My Bookings. No extra setup needed.
            </span>
          </label>

          {/* Service photos — up to 3 (before/after, sample-route, etc.) */}
          <div data-testid="service-photos-field">
            <p className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-1.5">
              Photos <span className="normal-case text-[#9A9A9A]">({form.photos.length}/3 — before/after, sample route, your rig)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {form.photos.map((ref, idx) => (
                <div key={`${ref}-${idx}`} className="relative w-20 h-20" data-testid={`service-photo-${idx}`}>
                  <img src={publicFileUrl(ref)} alt={`Service ${idx + 1}`} className="w-20 h-20 object-cover rounded-lg border border-[#EAEAEA]" />
                  <button
                    type="button"
                    onClick={() => removeServicePhoto(idx)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-[#E5E5E5] text-crimson flex items-center justify-center hover:bg-[#FFF0F0]"
                    aria-label="Remove photo"
                    data-testid={`service-photo-remove-${idx}`}
                  >
                    <Trash size={9} weight="bold" />
                  </button>
                </div>
              ))}
              {form.photos.length < 3 && (
                <button
                  type="button"
                  onClick={() => servicePhotoRef.current?.click()}
                  disabled={photoBusy}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E2DAC2] bg-cream/40 hover:bg-cream flex flex-col items-center justify-center text-[10px] text-[#8A6A00] disabled:opacity-50"
                  data-testid="service-photo-add"
                >
                  <UploadSimple size={16} weight="duotone" className="mb-1" />
                  {photoBusy ? "Uploading…" : "Add photo"}
                </button>
              )}
              <input
                ref={servicePhotoRef}
                type="file"
                accept="image/*"
                multiple
                onChange={onPickServicePhotos}
                className="hidden"
                data-testid="service-photo-input"
              />
            </div>
          </div>

          {form.kind === "recurring" && (
            <div className="bg-cream/40 border border-gold/30 rounded-lg p-3 space-y-3" data-testid="recurring-config-form">
              <p className="text-[11.5px] uppercase tracking-wider font-bold text-ink">Recurring package settings</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Cadences offered">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => toggleCadence("weekly")}
                      data-testid="cadence-weekly-toggle"
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        form.cadences.includes("weekly") ? "bg-ink text-white border-ink" : "bg-white border-[#EAEAEA] text-ink"
                      }`}
                    >Weekly</button>
                    <button
                      type="button"
                      onClick={() => toggleCadence("biweekly")}
                      data-testid="cadence-biweekly-toggle"
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        form.cadences.includes("biweekly") ? "bg-ink text-white border-ink" : "bg-white border-[#EAEAEA] text-ink"
                      }`}
                    >Bi-weekly</button>
                  </div>
                </Field>
                <Field label="Max sessions per series">
                  <Input type="number" min="2" max="12" value={form.max_sessions} onChange={(e) => setForm({ ...form, max_sessions: e.target.value })} data-testid="max-sessions-input" />
                </Field>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1.5">Bundle discounts (% off)</p>
                <p className="text-[11px] text-[#6B6B6B] mb-2 leading-snug">
                  Suggested: 5% off 4-pack, 10% off 8-pack, 15% off 12-pack. Set 0 to skip a tier.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="4-pack %">
                    <Input type="number" min="0" max="50" value={form.tier_4} onChange={(e) => setForm({ ...form, tier_4: e.target.value })} data-testid="tier-4" />
                  </Field>
                  <Field label="8-pack %">
                    <Input type="number" min="0" max="50" value={form.tier_8} onChange={(e) => setForm({ ...form, tier_8: e.target.value })} data-testid="tier-8" />
                  </Field>
                  <Field label="12-pack %">
                    <Input type="number" min="0" max="50" value={form.tier_12} onChange={(e) => setForm({ ...form, tier_12: e.target.value })} data-testid="tier-12" />
                  </Field>
                </div>
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <Button onClick={addService} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="service-save-btn">Save service</Button>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <div className="bg-white border border-[#EAEAEA] rounded-xl p-6 text-sm text-[#6B6B6B]" data-testid="services-empty">
          No services yet. Add at least one so customers know what you offer.
        </div>
      ) : (
        <div className="bg-white border border-[#EAEAEA] rounded-xl divide-y divide-[#F0F0F0]">
          {services.map((s) => (
            <div key={s.id} className="flex items-center gap-3 p-4" data-testid={`service-row-${s.id}`}>
              {Array.isArray(s.photos) && s.photos.length > 0 && (
                <img
                  src={publicFileUrl(s.photos[0])}
                  alt={s.name}
                  className="w-12 h-12 rounded-lg object-cover border border-[#EAEAEA] flex-shrink-0"
                  data-testid={`service-thumb-${s.id}`}
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink">{s.name}</p>
                <p className="text-[12.5px] text-[#6B6B6B] mt-0.5">
                  {s.duration_minutes} min · {s.kind === "recurring" ? "Recurring" : "One-time"}
                </p>
                {s.description && <p className="text-[12.5px] text-[#6B6B6B] mt-1 line-clamp-2">{s.description}</p>}
              </div>
              <span className="font-tabular font-semibold text-ink whitespace-nowrap">${s.price.toFixed(2)}</span>
              <button
                onClick={() => remove(s.id)}
                className="text-crimson hover:bg-[#FFF0F0] rounded-full w-8 h-8 flex items-center justify-center"
                data-testid={`service-delete-${s.id}`}
                aria-label="Remove service"
              >
                <Trash size={13} weight="duotone" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Stripe Connect status + onboarding launcher. Reuses the SAME
 * `/connect/account` Express account used by kennels — one
 * connected account per user. If they already onboarded as a
 * kennel/breeder, this surface flips straight to "ready" without
 * re-prompting them.
 */
function PayoutsCard() {
  const [acct, setAcct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [intlOpen, setIntlOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/connect/account");
      setAcct(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Native flow: Stripe onboarding happens in the system browser; when
  // the user swipes back to the app, re-check status so "Payouts
  // enabled ✓" appears without a manual reload.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/connect/account", { return_url: webOrigin() + "/connect/return?flow=provider" });
      if (isNativeApp()) {
        await openExternal(r.data.url);
        setBusy(false);
      } else {
        window.location.href = r.data.url;
      }
    } catch (e) {
      if (isIntlAgreementError(e)) {
        setIntlOpen(true);
      } else {
        toast.error(formatApiError(e) || "Could not start Stripe onboarding");
      }
      setBusy(false);
    }
  };

  const openDashboard = async () => {
    try {
      const r = await api.post("/connect/dashboard-link");
      await openExternal(r.data.url);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not open dashboard");
    }
  };

  if (loading) {
    return <div className="text-xs text-[#6B6B6B] mb-6">Loading payout status…</div>;
  }
  const onboarded = acct?.onboarded;
  const a = acct?.account || {};

  return (
    <div className="mb-8" data-testid="payouts-card">
      <IntlSellerAgreementDialog open={intlOpen} onOpenChange={setIntlOpen} onAccepted={start} />
      <h2 className="font-spectral text-2xl tracking-tight mb-3">Get paid</h2>
      <div className={`rounded-xl border p-4 ${onboarded ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-300"}`}>
        <div className="flex items-start gap-3">
          {onboarded ? (
            <CheckCircle size={22} weight="duotone" className="text-emerald-700 flex-shrink-0 mt-0.5" />
          ) : (
            <Warning size={22} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <p className={`font-semibold text-sm ${onboarded ? "text-emerald-900" : "text-amber-900"}`}>
              {onboarded ? "Stripe Connect — ready to accept bookings" : "Connect Stripe to receive payments"}
            </p>
            <p className={`text-[12.5px] mt-1 ${onboarded ? "text-emerald-900" : "text-amber-900"}`}>
              {onboarded
                ? `Booking payments will be deposited to your bank account on Stripe's standard payout schedule. Armstrong takes a 12% platform fee per booking; the remainder transfers directly to you.`
                : `Customers can browse your profile, but you can't accept paid bookings until your Stripe Connect account is verified. This is a 3-minute, one-time setup. Stripe handles the 1099-K at year end.`}
            </p>
            <div className="flex gap-2 mt-3 flex-wrap">
              {onboarded ? (
                <Button onClick={openDashboard} variant="outline" size="sm" className="rounded-full" data-testid="open-stripe-dashboard-btn">
                  Open Stripe dashboard <ArrowRight size={13} weight="bold" className="ml-1" />
                </Button>
              ) : (
                <Button onClick={start} disabled={busy} size="sm" className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="start-stripe-onboarding-btn">
                  {busy ? "Redirecting…" : (a.details_submitted ? "Resume Stripe setup" : "Connect with Stripe")}
                  <ArrowRight size={13} weight="bold" className="ml-1.5" />
                </Button>
              )}
            </div>
            {/* Sales tax setup nudge — relevant for providers selling
                tangible add-ons (grooming products, training treats) or
                for pure-service providers who want gross-revenue
                reporting in Stripe Tax. Always visible because
                "California doesn't tax most services" surprises some
                providers who think they don't need to know about Tax. */}
            <p
              className={`text-[12px] mt-3 leading-relaxed ${onboarded ? "text-emerald-900/80" : "text-amber-900/80"}`}
              data-testid="provider-tax-setup-note"
            >
              Selling tangible add-ons (treats, shampoo, training gear)?{" "}
              <Link
                to="/help/stripe-tax-setup"
                className="underline font-semibold hover:text-crimson"
                data-testid="provider-tax-setup-link"
              >
                See the Stripe Tax setup guide →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Weekly availability editor. We model availability as a list of
 * {weekday, start_time, end_time} windows — providers usually have
 * 1-2 windows per day (e.g. M-F 9-12 + 1-5). The "slots" endpoint
 * subtracts existing bookings on top of these windows.
 */
function AvailabilityManager() {
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/providers/mine/availability");
      setWindows(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addWindow = (weekday) => {
    setWindows([...windows, { id: `new-${Date.now()}`, weekday, start_time: "09:00", end_time: "17:00", active: true }]);
  };

  const updateWindow = (idx, patch) => {
    setWindows(windows.map((w, i) => (i === idx ? { ...w, ...patch } : w)));
  };

  const removeWindow = (idx) => {
    setWindows(windows.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        windows: windows.map((w) => ({
          weekday: w.weekday,
          start_time: w.start_time,
          end_time: w.end_time,
          active: w.active,
        })),
      };
      const r = await api.put("/providers/mine/availability", payload);
      setWindows(r.data || []);
      toast.success("Availability saved");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-xs text-[#6B6B6B] mb-6">Loading availability…</div>;

  const byDay = WEEKDAYS.map((_, i) => windows.filter((w) => w.weekday === i));

  return (
    <div className="mb-10" data-testid="availability-manager">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-spectral text-2xl tracking-tight flex items-center gap-2">
          <Calendar size={20} weight="duotone" className="text-gold" />
          Weekly availability
        </h2>
        <Button onClick={save} size="sm" disabled={saving} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="save-availability-btn">
          {saving ? "Saving…" : "Save availability"}
        </Button>
      </div>
      <p className="text-[12.5px] text-[#6B6B6B] mb-3 max-w-2xl">
        Tell us when you're open each week. The booking calendar will offer time slots inside these windows, minus any bookings already on the books.
      </p>
      <div className="bg-white border border-[#EAEAEA] rounded-xl divide-y divide-[#F0F0F0]">
        {WEEKDAYS.map((label, idx) => (
          <div key={idx} className="p-3 flex items-start gap-3" data-testid={`weekday-${idx}`}>
            <div className="w-12 flex-shrink-0">
              <p className="font-semibold text-ink text-sm">{label}</p>
            </div>
            <div className="flex-1 space-y-2">
              {byDay[idx].length === 0 ? (
                <p className="text-[12.5px] text-[#6B6B6B] italic">Closed</p>
              ) : (
                byDay[idx].map((w) => {
                  const globalIdx = windows.indexOf(w);
                  return (
                    <div key={w.id} className="flex items-center gap-2 flex-wrap">
                      <Input
                        type="time"
                        value={w.start_time}
                        onChange={(e) => updateWindow(globalIdx, { start_time: e.target.value })}
                        className="w-28"
                        data-testid={`start-${idx}-${globalIdx}`}
                      />
                      <span className="text-xs text-[#6B6B6B]">to</span>
                      <Input
                        type="time"
                        value={w.end_time}
                        onChange={(e) => updateWindow(globalIdx, { end_time: e.target.value })}
                        className="w-28"
                        data-testid={`end-${idx}-${globalIdx}`}
                      />
                      <button
                        onClick={() => removeWindow(globalIdx)}
                        className="text-crimson hover:bg-[#FFF0F0] rounded-full w-7 h-7 flex items-center justify-center"
                        aria-label="Remove window"
                        data-testid={`remove-window-${idx}-${globalIdx}`}
                      >
                        <Trash size={12} weight="duotone" />
                      </button>
                    </div>
                  );
                })
              )}
              <button
                onClick={() => addWindow(idx)}
                className="text-[11.5px] text-gold hover:underline inline-flex items-center gap-1"
                data-testid={`add-window-${idx}`}
              >
                <Plus size={11} weight="bold" /> Add hours
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * Insurance COI: provider uploads an image of their Certificate of
 * Coverage + self-reports carrier/expiration. Admin verifies in
 * `/admin/providers`. Re-uploading clears the verified flag so admin
 * re-reviews.
 */
function InsuranceCard({ provider, onRefresh }) {
  const ins = provider.insurance || null;
  const [carrier, setCarrier] = useState(ins?.carrier || "");
  const [policyNumber, setPolicyNumber] = useState(ins?.policy_number || "");
  const [expiresAt, setExpiresAt] = useState(ins?.expires_at || "");
  const [coverage, setCoverage] = useState(ins?.coverage_amount || "");
  const [coiUrl, setCoiUrl] = useState(ins?.coi_doc_url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = React.useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!file.type.startsWith("image/")) {
      toast.error("Upload an image of your COI (PDF→export as image, or take a clear photo).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const coi = r.data?.url || r.data?.storage_path;
      if (coi) {
        setCoiUrl(coi);
        toast.success("COI uploaded. Don't forget to save.");
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!expiresAt) return toast.error("Set the policy expiration date.");
    setSaving(true);
    try {
      await api.put("/providers/mine/insurance", {
        carrier,
        policy_number: policyNumber,
        expires_at: expiresAt,
        coverage_amount: coverage ? Number(coverage) : null,
        coi_doc_url: coiUrl || null,
      });
      toast.success("Insurance saved. Awaiting admin verification.");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-8" data-testid="insurance-card">
      <h2 className="font-spectral text-2xl tracking-tight mb-3 flex items-center gap-2">
        <ShieldCheck size={20} weight="duotone" className="text-gold" />
        Insurance Certificate
      </h2>
      {ins?.verified ? (
        <div className="rounded-xl border bg-emerald-50 border-emerald-200 p-3 mb-3 text-[12.5px] text-emerald-900 inline-flex items-center gap-2" data-testid="insurance-verified-banner">
          <CheckCircle size={14} weight="fill" />
          Verified by Armstrong{ins.verified_at ? ` on ${ins.verified_at.slice(0, 10)}` : ""}.
        </div>
      ) : ins?.expires_at ? (
        <div className="rounded-xl border bg-amber-50 border-amber-200 p-3 mb-3 text-[12.5px] text-amber-900 inline-flex items-center gap-2" data-testid="insurance-pending-banner">
          <Warning size={14} weight="duotone" />
          Self-reported — admin verification pending.
        </div>
      ) : null}
      <div className="bg-white border border-[#EAEAEA] rounded-xl p-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Carrier">
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="State Farm Business" data-testid="ins-carrier" />
          </Field>
          <Field label="Policy #">
            <Input value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} data-testid="ins-policy" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Expires (YYYY-MM-DD)" required>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="ins-expires" />
          </Field>
          <Field label="Coverage (USD)">
            <Input type="number" min="0" value={coverage} onChange={(e) => setCoverage(e.target.value)} placeholder="1000000" data-testid="ins-coverage" />
          </Field>
        </div>
        <Field label="Certificate of Coverage (image of your COI)">
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={inputRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="ins-coi-file" />
            <Button size="sm" type="button" variant="outline" className="rounded-full" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="ins-coi-upload-btn">
              <UploadSimple size={13} weight="bold" className="mr-1" /> {uploading ? "Uploading…" : (coiUrl ? "Replace COI" : "Upload COI")}
            </Button>
            {coiUrl && <a href={coiUrl} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline">View current COI</a>}
          </div>
        </Field>
        <Button onClick={save} disabled={saving} size="sm" className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="ins-save-btn">
          {saving ? "Saving…" : "Save insurance details"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Walkers + sitters require a paid background check (Option D hybrid):
 *   - Provider pays $39 at signup via Stripe Checkout
 *   - $25 is automatically credited back to their Stripe payouts
 *     after their first completed booking → net cost $14
 *   - Phase-3 ships a stubbed Checkr workflow: provider pays, status
 *     flips to 'requested', admin marks clear/fail until live API.
 */
function BackgroundCheckCard({ provider, onRefresh }) {
  const bg = provider.background_check || { status: "not_started" };
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/providers/mine/background-check/start", {});
      if (r.data?.already_started) {
        toast.info("Background check already in progress");
        onRefresh();
        return;
      }
      // Route to standard Stripe Checkout for the $39 SKU.
      const co = await api.post("/billing/checkout", {
        package_id: r.data.package_id,
        success_url: webOrigin() + "/provider/me?bg=success",
        cancel_url: webOrigin() + "/provider/me",
      });
      if (co.data?.checkout_url) {
        window.location.href = co.data.checkout_url;
        return;
      }
      // Free/comped path — backend fulfilled directly.
      toast.success("Background check submitted");
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not start");
    } finally {
      setBusy(false);
    }
  };

  // Phrase the credit line based on its current lifecycle stage.
  const creditLine = (() => {
    if (!bg.paid) return null;
    if (bg.credit_status === "granted") return `✓ $${(bg.credit_amount ?? 25).toFixed(0)} credited back to your payouts on ${(bg.credit_granted_at || "").slice(0, 10)}.`;
    if (bg.credit_status === "pending_first_booking") return `$${(bg.credit_amount ?? 25).toFixed(0)} credit will land in your payouts after your first completed booking.`;
    if (bg.credit_status === "deferred_no_connect") return `$${(bg.credit_amount ?? 25).toFixed(0)} credit is pending — connect Stripe to receive it.`;
    if (bg.credit_status === "failed") return `$${(bg.credit_amount ?? 25).toFixed(0)} credit failed to send — contact support.`;
    if (bg.credit_status === "skipped_not_first") return null;
    return null;
  })();

  const STATUS = {
    not_started: {
      label: "Not started",
      tone: "bg-[#FAFAFA] text-[#6B6B6B] border-[#EAEAEA]",
      body: (
        <>
          One-time <strong>$39</strong> background check (handled by an independent vendor). <strong>$25 is credited back</strong> to your payouts automatically after your first completed booking — net cost <strong>$14</strong> for active providers. Required for walkers + sitters to earn the "Background check cleared" trust badge.
        </>
      ),
    },
    requested: {
      label: "In progress",
      tone: "bg-amber-50 text-amber-900 border-amber-200",
      body: (
        <>
          Submitted. We'll email you when the check completes (typically 2–5 business days).
          {bg.checkr_invitation_url && (
            <span className="block mt-2 text-[12px]">
              Didn't get the Checkr email?{" "}
              <a href={bg.checkr_invitation_url} target="_blank" rel="noreferrer" className="underline font-semibold">
                Open your invitation
              </a>
            </span>
          )}
          {creditLine && <span className="block mt-1 text-[12px]">{creditLine}</span>}
        </>
      ),
    },
    clear: {
      label: "Cleared",
      tone: "bg-emerald-50 text-emerald-900 border-emerald-200",
      body: (
        <>
          Background check cleared. The "Background check cleared" badge now displays on your public profile.
          {creditLine && <span className="block mt-1 text-[12px]">{creditLine}</span>}
        </>
      ),
    },
    consider: {
      label: "Needs review",
      tone: "bg-amber-50 text-amber-900 border-amber-200",
      body: (
        <>
          Checkr returned 'consider' on one or more items. The Armstrong K9 Registry support team will follow up — no action needed from you right now. Your profile remains live and discoverable.
        </>
      ),
    },
    suspended: {
      label: "On hold",
      tone: "bg-amber-50 text-amber-900 border-amber-200",
      body: (
        <>
          Checkr paused your check, usually because additional information is needed from you.
          {bg.checkr_invitation_url && (
            <span className="block mt-2 text-[12px]">
              <a href={bg.checkr_invitation_url} target="_blank" rel="noreferrer" className="underline font-semibold">
                Open the Checkr portal
              </a>{" "}
              to resolve.
            </span>
          )}
        </>
      ),
    },
    fail: {
      label: "Did not clear",
      tone: "bg-rose-50 text-rose-900 border-rose-200",
      body: bg.admin_notes || "We were unable to verify this background check. Reach out to support to discuss.",
    },
  };
  const s = STATUS[bg.status] || STATUS.not_started;

  return (
    <div className="mb-8" data-testid="bg-check-card" id="bg-check-card">
      <h2 className="font-spectral text-2xl tracking-tight mb-3 flex items-center gap-2">
        <ShieldCheck size={20} weight="duotone" className="text-gold" />
        Background check
      </h2>
      <div className={`rounded-xl border p-4 ${s.tone}`}>
        <p className="font-semibold text-sm mb-1">{s.label}</p>
        <div className="text-[12.5px] leading-relaxed">{s.body}</div>
        {bg.status === "not_started" && (
          <Button onClick={start} disabled={busy} size="sm" className="rounded-full bg-ink hover:bg-crimson text-white mt-3" data-testid="start-bg-check-btn">
            {busy ? "Redirecting…" : "Pay $39 & start check"}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Reviews list with a single-response textarea per review. The
 * provider gets one chance to respond publicly to each review.
 */
function ReviewsManager({ provider }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/providers/mine/reviews");
      setReviews(r.data || []);
      const initial = {};
      (r.data || []).forEach((rv) => { initial[rv.id] = rv.response?.text || ""; });
      setDrafts(initial);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (review) => {
    try {
      await api.put(`/providers/mine/reviews/${review.id}/response`, { text: drafts[review.id] || "" });
      toast.success("Response posted");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not save response");
    }
  };

  if (loading) return null;

  return (
    <div className="mt-10" data-testid="reviews-manager">
      <h2 className="font-spectral text-2xl tracking-tight mb-3 flex items-center gap-3">
        Reviews
        {provider.rating?.count > 0 && (
          <span className="text-sm font-normal text-[#6B6B6B] inline-flex items-center gap-1.5">
            <StarRating value={provider.rating.avg} size={12} />
            <span className="font-tabular">{provider.rating.avg.toFixed(1)} · {provider.rating.count}</span>
          </span>
        )}
      </h2>
      {reviews.length === 0 ? (
        <div className="bg-white border border-[#EAEAEA] rounded-xl p-4 text-sm text-[#6B6B6B]" data-testid="reviews-empty">
          You haven't received any reviews yet. After you mark a booking complete, the customer can leave one.
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-[#EAEAEA] rounded-xl p-4" data-testid={`provider-review-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <StarRating value={r.rating} size={13} />
                <span className="text-[12.5px] font-semibold text-ink">{r.customer_name}</span>
                {r.service_name && <span className="text-[11.5px] text-[#6B6B6B]">· {r.service_name}</span>}
                <span className="text-[11px] text-[#9A9A9A] ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.text && <p className="text-[13px] text-ink leading-relaxed whitespace-pre-wrap">{r.text}</p>}
              <div className="mt-3 ml-4 pl-3 border-l-2 border-gold/40">
                <p className="text-[11px] uppercase tracking-wider font-bold text-gold mb-1.5">Your public response</p>
                <Textarea
                  rows={2}
                  value={drafts[r.id] || ""}
                  onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value.slice(0, 2000) })}
                  placeholder="Thank them, address any concerns, or stay quiet."
                  data-testid={`response-input-${r.id}`}
                />
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={() => save(r)} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid={`response-save-${r.id}`}>
                    {r.response?.text ? "Update response" : "Post response"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * "How you get paid" — plain-English fee math, visible ONLY to service
 * providers (backend 403s anyone else, so the card simply doesn't
 * render for other personas).
 */
function HowYouGetPaidCard() {
  const [fees, setFees] = useState(null);

  useEffect(() => {
    api.get("/providers/mine/fees", { silent: true }).then((r) => setFees(r.data)).catch(() => setFees(null));
  }, []);

  if (!fees) return null;
  const ex = fees.example || {};
  const feePct = Math.round((fees.platform_fee_pct || 0) * 100);
  const buyerPct = Math.round((fees.buyer_protection_pct || 0) * 100);
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-6 mb-6 shadow-luxury-light" data-testid="how-you-get-paid-card">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1">Money</p>
      <h2 className="font-spectral text-2xl tracking-tight">How you get paid</h2>
      <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed max-w-2xl">
        Clients pay through Stripe at booking. The money goes straight to <strong>your</strong> connected
        Stripe account minus the {feePct}% platform fee, then Stripe pays out to your bank automatically
        (typically 2-day rolling). Clients also pay a {buyerPct}% buyer-protection fee on top — that part
        never touches your price. Nothing to invoice, nothing to chase.
      </p>
      <div className="mt-4 border border-[#E2DAC2] rounded-lg bg-cream/40 p-4 max-w-md" data-testid="fee-example">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8A6A00] mb-2">
          Example: a ${Number(ex.price || 0).toFixed(0)} service
        </p>
        <div className="space-y-1.5 text-[13.5px]">
          <div className="flex justify-between"><span className="text-[#6B6B6B]">Client pays</span><span className="font-tabular font-semibold">${Number(ex.client_pays || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-[#6B6B6B]">You receive</span><span className="font-tabular font-semibold text-emerald-700" data-testid="fee-example-you-receive">${Number(ex.you_receive || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-[#6B6B6B]">Platform fee ({feePct}% + {buyerPct}% protection)</span><span className="font-tabular">${Number(ex.platform_keeps || 0).toFixed(2)}</span></div>
        </div>
      </div>
      <p className="text-[11.5px] text-[#8A8477] mt-3 leading-relaxed">
        Your payout lands before Stripe's standard card-processing cost. Track every payout under
        Payouts above.
      </p>
    </div>
  );
}

/**
 * Provider earnings rollup card — last 30/90/365 days + lifetime
 * net (after 12% platform fee) + upcoming revenue + by-month sparkline list.
 */
function EarningsCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/providers/mine/earnings").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const w = data.windows || {};
  const c = data.counts || {};
  const months = data.by_month || [];

  return (
    <div className="mb-8" data-testid="earnings-card">
      <h2 className="font-spectral text-2xl tracking-tight mb-3 flex items-center gap-2">
        <CurrencyDollar size={20} weight="duotone" className="text-gold" />
        Earnings
        <span className="text-xs font-normal text-[#6B6B6B]">· net of {data.platform_fee_pct}% platform fee</span>
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <StatBox label="Last 30 days" amount={w["30d"]} count={c["30d"]} testid="earn-30d" />
        <StatBox label="Last 90 days" amount={w["90d"]} count={c["90d"]} testid="earn-90d" />
        <StatBox label="Last 365 days" amount={w["365d"]} count={c["365d"]} testid="earn-365d" />
        <StatBox label="Lifetime" amount={w["lifetime"]} count={c["lifetime"]} testid="earn-lifetime" />
      </div>
      {data.upcoming?.count > 0 && (
        <div className="bg-cream border border-gold/35 rounded-xl p-3 mb-3 text-[12.5px] text-ink" data-testid="earn-upcoming">
          <strong>${data.upcoming.total.toFixed(2)}</strong> on the books across {data.upcoming.count} upcoming booking{data.upcoming.count === 1 ? "" : "s"}.
        </div>
      )}
      {months.length > 0 && (
        <div className="bg-white border border-[#EAEAEA] rounded-xl p-4">
          <p className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-3">By month</p>
          <div className="space-y-1.5">
            {months.map((m) => {
              const pct = Math.max(0, Math.min(100, (m.net / Math.max(1, months[0].net)) * 100));
              return (
                <div key={m.month} className="flex items-center gap-3" data-testid={`earn-month-${m.month}`}>
                  <span className="font-mono text-[11px] text-[#6B6B6B] w-16 flex-shrink-0">{m.month}</span>
                  <div className="flex-1 bg-[#FAFAFA] rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gold-gradient" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="font-tabular font-semibold text-ink text-sm w-24 text-right flex-shrink-0">
                    ${m.net.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, amount, count, testid }) {
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">{label}</p>
      <p className="font-spectral text-2xl text-ink mt-1">${Number(amount || 0).toFixed(2)}</p>
      <p className="text-[11px] text-[#6B6B6B]">{count || 0} booking{count === 1 ? "" : "s"}</p>
    </div>
  );
}

